import React, { useState, useMemo } from 'react';
import { MatchHighlights, HighlightWithPlayer, Highlight } from '../../types/highlights.types';
import { PlayerStats } from '../../pages/MatchPage';
import HighlightCard from './HighlightCard';
import FeaturedHighlight from './FeaturedHighlight';
import HighlightPlayer from './HighlightPlayer';
import ShareMenu from './ShareMenu';
import highlightsService from '../../services/highlightsService';
import '../../styles/components/highlights.scss';

interface HighlightsSectionProps {
  highlightsData: MatchHighlights;
  matchId: string;
  team1Players?: PlayerStats[];
  team2Players?: PlayerStats[];
}

const EMPTY_PLAYERS: PlayerStats[] = [];

const HighlightsSection: React.FC<HighlightsSectionProps> = ({
  highlightsData,
  matchId,
  team1Players = EMPTY_PLAYERS,
  team2Players = EMPTY_PLAYERS,
}) => {
  const [playingHighlight, setPlayingHighlight] = useState<HighlightWithPlayer | null>(null);
  const [sharingHighlight, setSharingHighlight] = useState<HighlightWithPlayer | null>(null);
  const [selectedFeatured, setSelectedFeatured] = useState<HighlightWithPlayer | null>(null);

  // Combine all players for lookup
  const allPlayers = useMemo(() => {
    return [...team1Players, ...team2Players];
  }, [team1Players, team2Players]);

  // Enrich highlights with player data
  const enrichedHighlights: HighlightWithPlayer[] = useMemo(() => {
    return highlightsData.highlights.map((highlight) => {
      const player = allPlayers.find((p) => p.steamID === highlight.steamId);
      return {
        ...highlight,
        playerName: player?.playerName || player?.name,
        playerAvatar: player?.avatarUrl,
        teamName: player?.team?.name,
      };
    });
  }, [highlightsData.highlights, allPlayers]);

  // Find initial featured highlight (highest priority tag)
  const initialFeatured = useMemo(() => {
    const priorityOrder = ['Ace', 'FourK', 'ThreeK', 'TwoK', 'OneK'];

    // Try to find by priority tags
    for (const tag of priorityOrder) {
      const found = enrichedHighlights.find(h => h.tags.includes(tag));
      if (found) return found;
    }

    // Fallback to first highlight
    return enrichedHighlights[0];
  }, [enrichedHighlights]);

  // Use selected featured or default to initial
  const featuredHighlight = selectedFeatured || initialFeatured;

  // Other highlights (excluding featured)
  const otherHighlights = useMemo(() => {
    return enrichedHighlights.filter(h => h.highlightId !== featuredHighlight?.highlightId);
  }, [enrichedHighlights, featuredHighlight]);

  // Handlers
  const handlePlayHighlight = (highlight: HighlightWithPlayer) => {
    setPlayingHighlight(highlight);
  };

  const handleSelectFeatured = (highlight: HighlightWithPlayer) => {
    setSelectedFeatured(highlight);
  };

  const handleClosePlayer = () => {
    setPlayingHighlight(null);
  };

  const handleNextHighlight = () => {
    if (!playingHighlight) return;
    const currentIndex = enrichedHighlights.findIndex(
      (h) => h.highlightId === playingHighlight.highlightId
    );
    if (currentIndex < enrichedHighlights.length - 1) {
      setPlayingHighlight(enrichedHighlights[currentIndex + 1]);
    }
  };

  const handlePreviousHighlight = () => {
    if (!playingHighlight) return;
    const currentIndex = enrichedHighlights.findIndex(
      (h) => h.highlightId === playingHighlight.highlightId
    );
    if (currentIndex > 0) {
      setPlayingHighlight(enrichedHighlights[currentIndex - 1]);
    }
  };

  const handleDownload = (highlight: HighlightWithPlayer) => {
    const filename = `${highlight.playerName || 'highlight'}-round${highlight.roundNumber}-${highlight.killCount}k.mp4`;
    highlightsService.downloadClip(highlight.clipUrl, filename);
  };

  const handleShare = (highlight: HighlightWithPlayer) => {
    setSharingHighlight(highlight);
  };

  const handleCloseShare = () => {
    setSharingHighlight(null);
  };

  // Get current highlight navigation state
  const getCurrentHighlightIndex = () => {
    if (!playingHighlight) return -1;
    return enrichedHighlights.findIndex(
      (h) => h.highlightId === playingHighlight.highlightId
    );
  };

  const currentIndex = getCurrentHighlightIndex();
  const hasNext = currentIndex >= 0 && currentIndex < enrichedHighlights.length - 1;
  const hasPrevious = currentIndex > 0;

  if (!highlightsData || highlightsData.highlightCount === 0 || !featuredHighlight) {
    return null;
  }

  return (
    <div className="highlights-section">
      <div className="highlights-section__header">
        <h2 className="highlights-section__title">Match Highlights</h2>
        <span className="highlights-section__count">{highlightsData.highlightCount} Clips</span>
      </div>

      <div className="highlights-section__layout">
        {/* Featured Highlight */}
        <div className="highlights-section__featured">
          <FeaturedHighlight
            highlight={featuredHighlight}
            onPlay={handlePlayHighlight}
            onDownload={handleDownload}
            onShare={handleShare}
          />
        </div>

        {/* Highlights List */}
        {otherHighlights.length > 0 && (
          <div className="highlights-section__list">
            {otherHighlights.map((highlight) => (
              <HighlightCard
                key={highlight.highlightId}
                highlight={highlight}
                variant="list"
                onPlay={handleSelectFeatured}
                onDownload={handleDownload}
                onShare={handleShare}
              />
            ))}
          </div>
        )}
      </div>

      {playingHighlight && (
        <HighlightPlayer
          highlight={playingHighlight}
          onClose={handleClosePlayer}
          onNext={hasNext ? handleNextHighlight : undefined}
          onPrevious={hasPrevious ? handlePreviousHighlight : undefined}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
        />
      )}

      {sharingHighlight && (
        <ShareMenu
          highlight={sharingHighlight}
          matchId={matchId}
          onClose={handleCloseShare}
        />
      )}
    </div>
  );
};

export default HighlightsSection;
